import { Avatar, AvatarFallback } from "@bowfast/ui/components/avatar";
import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent } from "@bowfast/ui/components/card";
import { Separator } from "@bowfast/ui/components/separator";
import { cn } from "@bowfast/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRightIcon, AwardIcon, ChevronDownIcon } from "lucide-react";
import { memo } from "react";
import { EASE_OUT } from "~/lib/animations";
import { gradientForId } from "~/lib/gradients";
import { initials } from "~/lib/roles";
import { AT_SEA_CONFIG, AVAILABLE_CONFIG, ROLE_CONFIG } from "~/pages/crew/crew.config";
import type { CrewListMember } from "~/pages/crew/crew.types";

interface CrewMemberCardProps {
  member: CrewListMember;
  isExpanded: boolean;
  onToggle: () => void;
}

export const CrewMemberCard = memo(function CrewMemberCard({ member, isExpanded, onToggle }: CrewMemberCardProps) {
  const primaryRole = member.roles[0];
  const roleConfig = primaryRole ? ROLE_CONFIG[primaryRole] : ROLE_CONFIG.CREW;
  const activeMembership = member.user.crewMemberships.find((cm) => cm.expedition.status === "ACTIVE");
  const presenceConfig = activeMembership ? AT_SEA_CONFIG : AVAILABLE_CONFIG;
  const memberInitials = initials(member.user.firstName, member.user.lastName);
  const gradient = gradientForId(member.user.id);
  const recentVoyages = [...member.user.crewMemberships]
    .sort((a, b) => (a.expedition.status === "ACTIVE" ? -1 : b.expedition.status === "ACTIVE" ? 1 : 0))
    .slice(0, 3);

  return (
    <motion.div layout transition={{ duration: 0.25, ease: EASE_OUT }}>
      <Card
        className={cn(
          "group cursor-pointer overflow-hidden transition-all duration-200",
          isExpanded
            ? "ring-2 ring-abyss-400/30 shadow-lg shadow-abyss-500/5"
            : "hover:shadow-md hover:border-border/80",
        )}
        onClick={onToggle}
      >
        <CardContent className="p-0">
          {/* ── Main row ── */}
          <div className="flex items-center gap-4 px-5 py-4">
            {/* Avatar with gradient ring + presence dot */}
            <div className="relative shrink-0">
              <div className={cn("rounded-full p-[2px] bg-gradient-to-br", gradient)}>
                <Avatar size="default" className="size-11 border-2 border-card">
                  <AvatarFallback className="bg-abyss-900 font-heading text-sm font-bold text-white">
                    {memberInitials}
                  </AvatarFallback>
                </Avatar>
              </div>
              <span
                className={cn("absolute bottom-0 right-0 size-3 rounded-full border-2 border-card", presenceConfig.dot)}
                title={presenceConfig.label}
              />
            </div>

            {/* Identity + cert highlight */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate font-heading text-[0.9375rem] font-bold tracking-tight text-foreground">
                  {member.user.displayName}
                </p>
                <Badge variant={roleConfig.variant} className="shrink-0 text-[0.625rem]">
                  {roleConfig.label}
                </Badge>
              </div>
              <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                <AwardIcon className="size-3 shrink-0" />
                Certificates not synced yet
              </p>
            </div>

            {/* Stats cluster */}
            <div className="hidden items-center gap-5 sm:flex">
              {/* Miles */}
              <div className="text-right">
                <p className="font-heading text-sm font-bold tabular-nums tracking-tight text-foreground">
                  {Math.round(member.user.totalMilesLogged).toLocaleString()}
                </p>
                <p className="text-[0.625rem] text-muted-foreground">nm logged</p>
              </div>

              <Separator orientation="vertical" className="!h-8" />

              {/* Expedition status */}
              <div className="w-32 text-right">
                {activeMembership ? (
                  <div>
                    <p className="flex items-center justify-end gap-1.5 text-sm font-semibold text-abyss-500">
                      <span className="relative flex size-2">
                        <span className="absolute inline-flex size-full animate-ping rounded-full bg-shallow-400 opacity-75" />
                        <span className="relative inline-flex size-2 rounded-full bg-shallow-400" />
                      </span>
                      {activeMembership.expedition.name}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Ashore</p>
                )}
              </div>
            </div>

            {/* Expand chevron */}
            <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }} className="shrink-0">
              <ChevronDownIcon className="size-4 text-muted-foreground/40" />
            </motion.div>
          </div>

          {/* ── Expanded detail panel ── */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: EASE_OUT }}
                className="overflow-hidden"
              >
                <div className="border-t border-border bg-muted/20 px-5 py-4">
                  <div className="grid gap-5 md:grid-cols-3">
                    {/* Certifications */}
                    <div>
                      <p className="mb-2 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                        Certifications
                      </p>
                      <p className="text-xs text-muted-foreground">Not synced yet</p>
                    </div>

                    {/* Recent voyages */}
                    <div>
                      <p className="mb-2 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                        Recent voyages
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {recentVoyages.map((cm) => (
                          <span
                            key={cm.id}
                            className={cn(
                              "rounded-full px-2.5 py-1 text-[0.6875rem] font-medium",
                              cm.expedition.status === "ACTIVE"
                                ? "bg-shallow-100 text-shallow-700 dark:bg-shallow-900/60 dark:text-shallow-200"
                                : "bg-muted text-foreground/70",
                            )}
                          >
                            {cm.expedition.status === "ACTIVE" && (
                              <span className="mr-1 inline-flex size-1.5 rounded-full bg-shallow-500" />
                            )}
                            {cm.expedition.name}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div className="flex w-56 flex-col gap-2 md:ml-auto">
                      <Button
                        size="sm"
                        className="w-full gap-2"
                        nativeButton={false}
                        render={<Link to="/crew/$memberId" params={{ memberId: member.user.id }} />}
                      >
                        View full profile
                        <ArrowRightIcon data-icon="inline-end" />
                      </Button>
                      <Button variant="outline" size="sm" className="w-full gap-2">
                        <AwardIcon className="size-3.5" />
                        Issue certificate
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
});
